import {
  bffCrmAppointmentsSelectAppointmentTypeGroups,
  BffCrmAppointmentsSelectAppointmentTypeGroupsRetrieveResponse,
  bffCrmAvailableAppointmentSlotsV2,
  bffCrmAvailableSlotsByProvider,
  BffCrmAvailableAppointmentSlotsV2RetrieveResponse,
  BffCrmAvailableSlotsByProviderRetrieveResponse,
  bffCrmCustomAppointmentAdditionalReasons,
} from '@fireflyhealth/api'
import { endOfDay } from 'date-fns'
import { useQuery } from '~/shared/hooks/query'
import { required } from '~/shared/lib/required'
export type AppointmentTypeGroup =
  BffCrmAppointmentsSelectAppointmentTypeGroupsRetrieveResponse['groups'][number]

/**
 * A visit type as the server names it: an appointment type's `unique_key`.
 * The server-driven wizard carries this value end to end, so the booking
 * request sends it back unchanged.
 */
export type ServerVisitReason = AppointmentTypeGroup['appointment_types'][number]['unique_key']

/**
 * A visit type as the legacy wizard names it. Each key translates to its wire
 * value through `AppointmentTypeMapping`.
 */
export type LegacyVisitReason = keyof typeof AppointmentTypeMapping

/**
 * What the wizard's selection state holds. The two key families are disjoint,
 * so a value identifies which wizard shape produced it (`isServerVisitReason`).
 * The legacy arm goes away with the legacy menu.
 */
export type VisitReason = LegacyVisitReason | ServerVisitReason

type AvailableAppointmentSlotsV2Response = BffCrmAvailableAppointmentSlotsV2RetrieveResponse

// A provider row from the v2 slots response, plus the synthesized `scheduled_date`
// set when the user picks a single time (the response itself never returns it). A
// selected slot only carries the fields it was built from, so the generated row
// fields beyond the always-present `physician_id`/`reason` are optional here.
export type AvailablePhysicianSlot = Partial<
  AvailableAppointmentSlotsV2Response['appointments'][number]
> & {
  physician_id: number
  reason: string
  scheduled_date?: string
}

export const AppointmentTypeMapping = {
  video: 'Video',
  videoNew: 'Video-New',
  healthGuideConsult: 'Health Guide Consult',
  behavioralHealth: 'Behavioral Health',
  focused: 'Focused',
  annualWellnessVisitEstablished: 'Annual Wellness Visit Established',
  annualWellnessVisitNew: 'Annual Wellness Visit New',
} as const satisfies Record<string, ServerVisitReason>

export const isServerVisitReason = (reason: VisitReason): reason is ServerVisitReason =>
  !(reason in AppointmentTypeMapping)

/** The `reason` the slot and booking endpoints take, from either wizard's key. */
export const toWireReason = (reason: VisitReason): ServerVisitReason =>
  isServerVisitReason(reason) ? reason : AppointmentTypeMapping[reason]

/**
 * The appointment type groups the member is eligible for, plus why nothing is
 * bookable when that is the case. The booking menu renders the groups while
 * `appointment.crm_server_driven_appointment_types` is active. The response
 * holds only eligible types, so a group can arrive with fewer types than its
 * definition declares.
 */
export const useAppointmentTypeGroups = (patientId: number) =>
  useQuery(
    bffCrmAppointmentsSelectAppointmentTypeGroups.retrieveQueryKey({ patient_id: patientId }),
    async () => {
      const response = await bffCrmAppointmentsSelectAppointmentTypeGroups.retrieve({
        params: { patient_id: patientId },
      })
      return response.data
    },
    { staleTime: Infinity, enabled: !!patientId },
  )

const stripMilliseconds = (date: Date) => date.toISOString().replace(/\.\d+/, '')

const availablePhysiciansWithSlotsV2 = async (
  patientId: number,
  reason: VisitReason,
  from: Date,
  to: Date,
): Promise<AvailableAppointmentSlotsV2Response> => {
  const response = await bffCrmAvailableAppointmentSlotsV2.retrieve({
    params: {
      patient_id: patientId,
      reason: toWireReason(reason),
      start_time: stripMilliseconds(from),
      end_time: stripMilliseconds(to),
    },
  })
  return response.data
}

// Shared query for the CRM book-appointment V2 slots: the By-Day tab reads slots, the parent reads the
// patient's complexity-adjusted length. Same key -> React Query dedupes to a single request.
const availablePhysiciansWithSlotsV2QueryKey = (
  patientId: number,
  reason: VisitReason | null,
  date: Date,
) => ['availablePhysiciansWithSlotsV2', patientId, reason, date]

// `enabled` lets the parent gate this to the active tab so it isn't fetched purely for the length while
// the By Provider tab (which sources its own length) is showing.
export const useAvailablePhysiciansWithSlotsV2 = (
  patientId: number,
  reason: VisitReason | null,
  date: Date,
  enabled = true,
) =>
  useQuery(
    availablePhysiciansWithSlotsV2QueryKey(patientId, reason, date),
    () =>
      availablePhysiciansWithSlotsV2(
        patientId,
        required(reason, 'a visit reason'),
        date,
        endOfDay(date),
      ),
    { enabled: !!reason && enabled },
  )

export type AvailableSlotsByProviderResponse = BffCrmAvailableSlotsByProviderRetrieveResponse

export const availableSlotsByProvider = async (
  physicianId: number,
  patientId: number,
  reason: VisitReason,
  from: Date,
  to: Date,
): Promise<AvailableSlotsByProviderResponse> => {
  const response = await bffCrmAvailableSlotsByProvider.retrieve({
    params: {
      physician_id: physicianId,
      patient_id: patientId,
      reason: toWireReason(reason),
      start_time: stripMilliseconds(from),
      end_time: stripMilliseconds(to),
    },
  })
  return response.data
}

export const getAdditionalReasonsForCustomAppointment = async (payload: {
  physicianId: number
  patientId?: number
  reason: string
  appointmentDateTime: string
}) => {
  const response = await bffCrmCustomAppointmentAdditionalReasons.retrieve({
    params: {
      appointment_date_time: payload.appointmentDateTime,
      physician_id: payload.physicianId,
      patient_id: payload.patientId,
      reason: payload.reason,
    },
  })
  return response.data
}
